/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { isPresent } from '@ember/utils';
import { next } from '@ember/runloop';
import { Dropdown as BasicDropdown } from 'ember-basic-dropdown/components/basic-dropdown';
import { EnvironmentOption } from 'cio/pods/app/controller';
import { flyout } from '@customerio/fly-components/utils/dropdown-positions';
import { FlyKeyboardEvent } from '@customerio/fly-components/types/fly-components/events';

const WORKSPACE_BUTTON_TARGET = '.js-workspace-button';

interface WorkspaceDropdownArgs {
  environment: EnvironmentOption;
  environments: EnvironmentOption[];
  onSelect: (environment: EnvironmentOption) => unknown;
  onOpen: () => unknown;
  onClose: () => unknown;
}

export default class WorkspaceDropdown extends Component<WorkspaceDropdownArgs> {
  calculateFlyoutPosition = flyout;

  @tracked contentElement: HTMLElement | null = null;
  @tracked searchTerm = '';

  get filteredEnvironments() {
    return isPresent(this.searchTerm)
      ? this.args.environments.filter((env) => env.label.toLowerCase().includes(this.searchTerm.toLowerCase()))
      : this.args.environments;
  }

  @action
  setContentElement(element: HTMLDivElement) {
    this.contentElement = element;

    // Do this on the next tick, because ember-basic-dropdown forces a refocus on the trigger
    next(this, () => {
      let input = element.querySelector('input');
      let workspaceControls = element.querySelectorAll<HTMLAnchorElement>(WORKSPACE_BUTTON_TARGET);

      // Focus on the input first, otherwise on the first workspace in the list
      if (input) {
        input.focus();
      } else {
        workspaceControls[0]?.focus();
      }
    });
  }

  // Track when the user uses their arrow keys to move up and down in the list with keybaord navigation or if they press
  // their escape key to close the menu
  @action
  onKeyDown(dropdown: BasicDropdown, event: FlyKeyboardEvent<HTMLElement>) {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.focusPrevious(event.target);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.focusNext(event.target);
    } else if (event.key === 'Escape') {
      dropdown.actions.close();
    }
  }

  // Focus the keyboard on the next item in the list when the user uses their arrow keys
  // If it's at the first item already, return to the last item in the list
  @action
  focusPrevious(focusElement: HTMLElement) {
    // Previous sibling of the parent LI. If the input is in focus, just to go the bottom of the list
    if (focusElement?.parentElement?.previousElementSibling && focusElement.tagName !== 'INPUT') {
      focusElement.parentElement.previousElementSibling
        .querySelector<HTMLButtonElement>(WORKSPACE_BUTTON_TARGET)
        ?.focus();
    } else {
      let workspaceControls = this.contentElement?.querySelectorAll<HTMLButtonElement>(WORKSPACE_BUTTON_TARGET) ?? [];

      workspaceControls[workspaceControls.length - 1]?.focus();
    }
  }

  // Focus the keyboard on the next item in the list when the user uses their arrow keys
  // If it's at the last item already, return to the first item in the list
  @action
  focusNext(focusElement: HTMLElement) {
    // Next sibling of the parent LI. If the input is in focus, just to go the top of the list
    if (focusElement?.parentElement?.nextElementSibling && focusElement.tagName !== 'INPUT') {
      focusElement.parentElement.nextElementSibling.querySelector<HTMLButtonElement>(WORKSPACE_BUTTON_TARGET)?.focus();
    } else {
      let workspaceControls = this.contentElement?.querySelectorAll<HTMLButtonElement>(WORKSPACE_BUTTON_TARGET) ?? [];

      workspaceControls[0]?.focus();
    }
  }

  @action
  onClose() {
    this.searchTerm = '';

    this.args.onClose();
  }

  @action
  selectEnvironment(dropdown: BasicDropdown, option: EnvironmentOption) {
    dropdown.actions.close();
    this.args.onSelect(option);
  }

  // An unpatched and closed issue with BasicDropdown causes an app reload when
  // a link is clicked and the dropdown closed at the same time. This defers
  // the close to another tick. https://github.com/cibernox/ember-basic-dropdown/issues/528
  @action
  handleLinkClick(dropdown: BasicDropdown) {
    next(this, () => dropdown.actions.close());
  }
}
